// eslint-disable-next-line no-restricted-imports
import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {getSelectedThread} from '@github-ui/copilot-chat/utils/get-selected-thread'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useCallback} from 'react'

import type {TimelineEventTextReference} from '../../../timeline-events/timeline-events-types'
import {
  type DraftOrExistingIssue,
  makeReferenceFromExistingIssue,
  makeReferenceFromVersionedItem,
} from '../../content-preview-types'
import {useContentPreview} from '../../ContentPreviewContext'

export function useRemoveDependencyCallback() {
  const manager = useChatManager()
  const state = useChatState()
  const {updateItem} = useContentPreview()

  return useCallback(
    async (blocked: DraftOrExistingIssue, blocking: DraftOrExistingIssue) => {
      sendEvent('dotcom_chat.activate', {
        target: 'BROWSER_ISSUE_REMOVE_DEPENDENCY',
        mode: 'immersive',
        threadId: getSelectedThread(state)?.id,
        chatReferrer: document.referrer,
        issueTag: blocked.tag,
        issueRepository: blocked.repository,
        blockingIssueTag: blocking.tag,
      })

      const instructions: TimelineEventTextReference = {
        type: 'text',
        name: `timeline-event: {"type": "remove-dependency", "markdownContent": "Removed dependency on '${blocking.name}'"}`,
        text: `Remove the dependency between '${blocked.tag}' and '${blocking.tag}'. Return the updated issues.`,
      }

      const updatedBlockedIssue = {
        ...blocked,
        blockedByTags: blocked.blockedByTags?.filter(tag => tag !== blocking.tag),
        isUserEdited: false,
      }
      updateItem(updatedBlockedIssue)
      const updatedBlockingIssue = {
        ...blocking,
        isBlockingTags: blocking.isBlockingTags?.filter(tag => tag !== blocked.tag),
        isUserEdited: false,
      }
      updateItem(updatedBlockingIssue)

      const updatedIssueRefs = [updatedBlockedIssue, updatedBlockingIssue].map(issue => {
        return issue.type === 'existing-issue'
          ? makeReferenceFromExistingIssue(issue)
          : makeReferenceFromVersionedItem(issue)
      })

      await manager.sendChatMessage({
        thread: getSelectedThread(state),
        content: 'Remove the blocking dependency from the issue',
        references: [instructions, ...updatedIssueRefs],
        topic: state.currentTopic,
        context: state.context,
        customInstructions: state.customInstructions,
        model: state.model,
      })
    },
    [manager, state, updateItem],
  )
}
